import { useState, useEffect, useCallback, Fragment, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Loader2, ChevronRight, ChevronLeft, Check, AlertTriangle, GripVertical,
  ArrowUp, ArrowDown, RefreshCw, Lock, Search, X
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const PRI_COLORS = {
  1: { bg: '#ef444420', text: '#ef4444', label: 'P1 (Critical)' },
  2: { bg: '#f59e0b20', text: '#f59e0b', label: 'P2 (High)' },
  3: { bg: '#3b82f620', text: '#3b82f6', label: 'P3 (Medium)' },
  4: { bg: '#143A6B20', text: '#143A6B', label: 'P4 (Low)' },
  5: { bg: '#94a3b820', text: '#94a3b8', label: 'P5' },
};

function fmtNum(n) { return n != null ? new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n) : '-'; }

export default function PWMPriorityWizard({ projectId, onClose }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [assignments, setAssignments] = useState({});  // lineId → { priority, subPriority }
  const [overrides, setOverrides] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [bulkPriority, setBulkPriority] = useState(2);
  const [areaPriority, setAreaPriority] = useState({ area: '', agUg: '', priority: 3 });
  const [overrideForm, setOverrideForm] = useState({ lineId: '', priority: 1, reason: '', docRef: '', urgency: 'NORMAL' });
  const [dragItem, setDragItem] = useState(null);
  const [subPriLevel, setSubPriLevel] = useState(1);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/v1/projects/${projectId}/pwm/priority/wizard/prepare`);
      setData(res.data);
      // Init assignments from current priorities
      const init = {};
      for (const l of (res.data?.lines || [])) {
        if (l.priority != null) {
          init[l.lineId] = { priority: l.priority, subPriority: l.subPriority || 1 };
        }
      }
      setAssignments(init);
    } catch { toast.error('Failed to load wizard data'); }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  const allLines = data?.lines || [];
  const areas = [...new Set(allLines.map(l => l.area).filter(Boolean))].sort();

  const getEffectivePriority = (lineId) => {
    if (assignments[lineId]) return assignments[lineId].priority;
    const l = allLines.find(x => x.lineId === lineId);
    return l?.priority;
  };

  const getEffectiveSubPri = (lineId) => {
    if (assignments[lineId]) return assignments[lineId].subPriority;
    const l = allLines.find(x => x.lineId === lineId);
    return l?.subPriority;
  };

  const setLinePriority = (lineId, priority, subPriority) => {
    setAssignments(prev => ({ ...prev, [lineId]: { priority, subPriority: subPriority || 1 } }));
  };

  const assignBulk = () => {
    const updated = { ...assignments };
    selected.forEach(lid => { updated[lid] = { priority: bulkPriority, subPriority: 1 }; });
    setAssignments(updated);
    setSelected(new Set());
    toast.success(`Assigned P${bulkPriority} to ${selected.size} lines`);
  };

  const assignArea = async () => {
    if (!areaPriority.area) return;
    const matching = allLines.filter(l => l.area === areaPriority.area && l.priority == null && (!areaPriority.agUg || l.agUg === areaPriority.agUg));
    const updated = { ...assignments };
    matching.forEach(l => { updated[l.lineId] = { priority: areaPriority.priority, subPriority: 1 }; });
    setAssignments(updated);
    toast.success(`Assigned P${areaPriority.priority} to ${matching.length} lines in ${areaPriority.area}`);
  };

  const applyOverride = () => {
    if (!overrideForm.lineId || !overrideForm.reason) return;
    setLinePriority(overrideForm.lineId, overrideForm.priority, 1);
    setOverrides(prev => [...prev, { ...overrideForm }]);
    toast.success(`Override applied to ${overrideForm.lineId}`);
  };

  const autoSubPriority = (mode) => {
    const updated = { ...assignments };
    const lines = allLines.filter(l => getEffectivePriority(l.lineId) === subPriLevel);
    let sorted;
    if (mode === 'inch-dia') {
      sorted = [...lines].sort((a, b) => (b.totalLineInchDia || 0) - (a.totalLineInchDia || 0));
    } else if (mode === 'area') {
      sorted = [...lines].sort((a, b) => (a.area || '').localeCompare(b.area || '') || (b.totalLineInchDia || 0) - (a.totalLineInchDia || 0));
    } else {
      sorted = lines;
    }
    sorted.forEach((l, i) => { updated[l.lineId] = { priority: subPriLevel, subPriority: i + 1 }; });
    setAssignments(updated);
    toast.success(`Auto-assigned sub-priorities for P${subPriLevel} (${sorted.length} lines)`);
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      // First apply current assignments to backend
      const assignList = Object.entries(assignments).map(([lineId, a]) => ({
        lineId, priority: a.priority, subPriority: a.subPriority,
      }));
      for (const a of assignList) {
        await api.post(`/v1/projects/${projectId}/pwm/priority/wizard/assign-single`, a);
      }
      const res = await api.get(`/v1/projects/${projectId}/pwm/priority/wizard/impact-preview`);
      setPreview(res.data);
    } catch { toast.error('Failed to load preview'); }
    setPreviewLoading(false);
  };

  const applyAll = async () => {
    setApplying(true);
    try {
      const assignList = Object.entries(assignments).map(([lineId, a]) => ({
        lineId, priority: a.priority, subPriority: a.subPriority,
        isOverride: overrides.some(o => o.lineId === lineId),
        overrideReason: overrides.find(o => o.lineId === lineId)?.reason,
        overrideDocumentRef: overrides.find(o => o.lineId === lineId)?.docRef,
      }));
      const res = await api.post(`/v1/projects/${projectId}/pwm/priority/wizard/finalize`, {
        assignments: assignList, triggerAllocationImmediately: true,
      });
      toast.success(`Applied ${res.data.assignmentsCreated} assignments. Allocation triggered.`);
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to apply'); }
    setApplying(false);
  };

  const toggleSelect = (lineId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId); else next.add(lineId);
      return next;
    });
  };

  // Priority columns for drag board (Step 2)
  const priColumns = {};
  const unassignedPool = [];
  allLines.forEach(l => {
    const p = getEffectivePriority(l.lineId);
    if (p == null) { unassignedPool.push(l); return; }
    if (!priColumns[p]) priColumns[p] = [];
    priColumns[p].push(l);
  });
  // Sort each column by inch-dia DESC
  Object.values(priColumns).forEach(arr => arr.sort((a, b) => (b.totalLineInchDia || 0) - (a.totalLineInchDia || 0)));

  // Step 3 lines for current sub-pri level
  const subPriLines = allLines.filter(l => getEffectivePriority(l.lineId) === subPriLevel)
    .sort((a, b) => (getEffectiveSubPri(a.lineId) || 999) - (getEffectiveSubPri(b.lineId) || 999));

  // Filter for search
  const filteredLines = allLines.filter(l => !search || l.lineId?.toLowerCase().includes(search.toLowerCase()));

  // Priority dist chart data
  const distData = [1, 2, 3, 4, 5].map(p => ({
    name: `P${p}`, count: allLines.filter(l => getEffectivePriority(l.lineId) === p).length,
    fill: PRI_COLORS[p]?.text || '#94a3b8',
  }));
  distData.push({
    name: 'Unassigned',
    count: allLines.filter(l => getEffectivePriority(l.lineId) == null).length,
    fill: '#ef4444',
  });

  const handleDragStart = (e, lineId) => { setDragItem(lineId); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = (e, priority) => {
    e.preventDefault();
    if (dragItem) { setLinePriority(dragItem, priority, 1); setDragItem(null); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

  const steps = [
    { n: 1, label: 'Review Lines' },
    { n: 2, label: 'Assign Priorities' },
    { n: 3, label: 'Sub-Priority' },
    { n: 4, label: 'Impact Preview' },
    { n: 5, label: 'Confirm & Apply' },
  ];

  const assignedCount = allLines.filter(l => getEffectivePriority(l.lineId) != null).length;
  const unassignedCount = allLines.length - assignedCount;

  return (
    <div className="space-y-3" data-testid="priority-wizard">
      {/* Step Indicator */}
      <div className="flex items-center gap-1 p-2 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
        {steps.map((s, i) => (
          <Fragment key={s.n}>
            {i > 0 && <ChevronRight className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />}
            <button onClick={() => setStep(s.n)} data-testid={`wizard-step-${s.n}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-medium transition-all"
              style={{ background: step === s.n ? '#143A6B' : step > s.n ? '#22c55e15' : 'transparent',
                       color: step === s.n ? '#fff' : step > s.n ? '#22c55e' : 'var(--text-muted)' }}>
              <span className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold"
                style={{ background: step === s.n ? '#fff' : step > s.n ? '#22c55e' : 'var(--border-color)',
                         color: step === s.n ? '#143A6B' : step > s.n ? '#fff' : 'var(--text-muted)' }}>
                {step > s.n ? <Check className="w-2.5 h-2.5" /> : s.n}
              </span>
              {s.label}
            </button>
          </Fragment>
        ))}
      </div>

      {/* ═══ STEP 1: REVIEW ═══ */}
      {step === 1 && (
        <div className="space-y-3" data-testid="wizard-step1">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {[
              { label: 'Total Lines', val: data?.totalLines, color: '#143A6B' },
              { label: 'Assigned', val: assignedCount, color: '#22c55e' },
              { label: 'Unassigned', val: unassignedCount, color: '#ef4444' },
              { label: 'AG Lines', val: data?.agCount, color: '#3b82f6' },
              { label: 'UG Lines', val: data?.ugCount, color: '#f59e0b' },
              { label: 'Overrides', val: overrides.length, color: '#ec4899' },
            ].map(c => (
              <div key={c.label} className="p-2.5 rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
                <p className="text-[9px] font-medium" style={{ color: 'var(--text-muted)' }}>{c.label}</p>
                <p className="text-lg font-bold" style={{ color: c.color }}>{fmtNum(c.val)}</p>
              </div>
            ))}
          </div>

          <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <p className="text-[10px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Priority Distribution</p>
            <div style={{ height: 140 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={distData} margin={{ top: 8, right: 12, left: -10, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 9, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 11 }} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={45}>
                    {distData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="relative max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search line ID..."
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>

          <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border-color)', maxHeight: 350 }}>
            <table className="w-full text-[10px]">
              <thead>
                <tr style={{ background: 'var(--card-bg-alt)', position: 'sticky', top: 0, zIndex: 2 }}>
                  <th className="p-1.5 w-8"><input type="checkbox" className="rounded" onChange={e => {
                    if (e.target.checked) setSelected(new Set(filteredLines.map(l => l.lineId)));
                    else setSelected(new Set());
                  }} /></th>
                  <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Line ID</th>
                  <th className="p-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>AG/UG</th>
                  <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Area</th>
                  <th className="p-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Total ID</th>
                  <th className="p-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Spools</th>
                  <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Fab Loc</th>
                  <th className="p-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Priority</th>
                  <th className="p-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Sub-P</th>
                  <th className="p-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredLines.sort((a, b) => {
                  const pa = getEffectivePriority(a.lineId);
                  const pb = getEffectivePriority(b.lineId);
                  if (pa == null && pb != null) return -1;
                  if (pa != null && pb == null) return 1;
                  return 0;
                }).map(l => {
                  const p = getEffectivePriority(l.lineId);
                  const isUnassigned = p == null;
                  return (
                    <tr key={l.lineId} style={{ borderTop: '1px solid var(--border-color)', background: isUnassigned ? '#f59e0b08' : 'var(--card-bg)' }}>
                      <td className="p-1.5"><input type="checkbox" checked={selected.has(l.lineId)} onChange={() => toggleSelect(l.lineId)} className="rounded" /></td>
                      <td className="p-1.5 font-mono font-semibold" style={{ color: 'var(--accent-primary)' }}>{l.lineId}</td>
                      <td className="p-1.5 text-center" style={{ color: l.agUg === 'AG' ? '#3b82f6' : '#f59e0b' }}>{l.agUg}</td>
                      <td className="p-1.5" style={{ color: 'var(--text-muted)' }}>{l.area}</td>
                      <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(l.totalLineInchDia)}</td>
                      <td className="p-1.5 text-center" style={{ color: 'var(--text-muted)' }}>{l.totalComponentCount || '-'}</td>
                      <td className="p-1.5" style={{ color: 'var(--text-muted)' }}>{l.fabricationLocation}</td>
                      <td className="p-1.5 text-center">
                        {isUnassigned ? (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: '#f59e0b20', color: '#f59e0b' }}>Unassigned</span>
                        ) : (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: PRI_COLORS[p]?.bg, color: PRI_COLORS[p]?.text }}>P{p}</span>
                        )}
                      </td>
                      <td className="p-1.5 text-center font-mono" style={{ color: 'var(--text-muted)' }}>{getEffectiveSubPri(l.lineId) || '-'}</td>
                      <td className="p-1.5 text-center text-[9px]" style={{ color: l.workfrontStatus === 'UNALLOCATED' ? '#ef4444' : '#22c55e' }}>{l.workfrontStatus?.replace(/_/g, ' ')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ STEP 2: ASSIGN PRIORITIES ═══ */}
      {step === 2 && (
        <div className="space-y-3" data-testid="wizard-step2">
          {/* Drag-and-drop Priority Board */}
          <div className="grid grid-cols-5 gap-2" data-testid="priority-board">
            {[1, 2, 3, 4, 5].map(p => {
              const col = priColumns[p] || [];
              const pc = PRI_COLORS[p];
              return (
                <div key={p} className="rounded-lg border p-2 min-h-[200px]"
                  style={{ borderColor: pc.text + '40', background: pc.bg }}
                  onDragOver={handleDragOver} onDrop={e => handleDrop(e, p)}
                  data-testid={`priority-col-${p}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold" style={{ color: pc.text }}>{pc.label}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: pc.text, color: '#fff' }}>{col.length}</span>
                  </div>
                  <div className="space-y-1 max-h-[250px] overflow-y-auto">
                    {col.slice(0, 20).map(l => (
                      <div key={l.lineId} draggable onDragStart={e => handleDragStart(e, l.lineId)}
                        className="p-1.5 rounded border cursor-grab text-[9px]"
                        style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
                        <span className="font-mono font-semibold" style={{ color: 'var(--accent-primary)' }}>{l.lineId}</span>
                        <div className="flex gap-2 mt-0.5" style={{ color: 'var(--text-muted)' }}>
                          <span style={{ color: l.agUg === 'AG' ? '#3b82f6' : '#f59e0b' }}>{l.agUg}</span>
                          <span>{fmtNum(l.totalLineInchDia)} ID</span>
                        </div>
                      </div>
                    ))}
                    {col.length > 20 && <p className="text-[8px] text-center" style={{ color: 'var(--text-muted)' }}>+{col.length - 20} more</p>}
                  </div>
                  <p className="text-[8px] mt-1" style={{ color: 'var(--text-muted)' }}>
                    ID: {fmtNum(col.reduce((s, l) => s + (l.totalLineInchDia || 0), 0))}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Unassigned Pool */}
          {unassignedPool.length > 0 && (
            <div className="rounded-lg border p-2" style={{ borderColor: '#f59e0b40', background: '#f59e0b08' }}
              onDragOver={handleDragOver} onDrop={e => handleDrop(e, null)} data-testid="unassigned-pool">
              <p className="text-[10px] font-bold mb-1" style={{ color: '#f59e0b' }}>
                Unassigned ({unassignedPool.length}) — Drag into priority columns above
              </p>
              <div className="flex flex-wrap gap-1 max-h-[120px] overflow-y-auto">
                {unassignedPool.map(l => (
                  <div key={l.lineId} draggable onDragStart={e => handleDragStart(e, l.lineId)}
                    className="p-1 rounded border cursor-grab text-[9px]" style={{ borderColor: '#f59e0b40', background: 'var(--card-bg)' }}>
                    <span className="font-mono font-semibold" style={{ color: 'var(--accent-primary)' }}>{l.lineId}</span>
                    <span className="ml-1" style={{ color: 'var(--text-muted)' }}>{l.agUg} {fmtNum(l.totalLineInchDia)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bulk Assign */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="p-2.5 rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
              <p className="text-[10px] font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Bulk Selection ({selected.size})</p>
              <div className="flex items-center gap-2">
                <select value={bulkPriority} onChange={e => setBulkPriority(Number(e.target.value))}
                  className="text-[10px] px-2 py-1 rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                  {[1,2,3,4,5].map(p => <option key={p} value={p}>P{p}</option>)}
                </select>
                <Button size="sm" onClick={assignBulk} disabled={selected.size === 0} className="text-[10px]" data-testid="bulk-assign-btn">Apply to Selected</Button>
              </div>
            </div>

            <div className="p-2.5 rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
              <p className="text-[10px] font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Area-Wide Assign</p>
              <div className="flex items-center gap-1">
                <select value={areaPriority.area} onChange={e => setAreaPriority(p => ({ ...p, area: e.target.value }))}
                  className="text-[10px] px-2 py-1 rounded border flex-1" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                  <option value="">Area</option>
                  {areas.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <select value={areaPriority.agUg} onChange={e => setAreaPriority(p => ({ ...p, agUg: e.target.value }))}
                  className="text-[10px] px-2 py-1 rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                  <option value="">All</option><option value="AG">AG</option><option value="UG">UG</option>
                </select>
                <select value={areaPriority.priority} onChange={e => setAreaPriority(p => ({ ...p, priority: Number(e.target.value) }))}
                  className="text-[10px] px-2 py-1 rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                  {[1,2,3,4,5].map(p => <option key={p} value={p}>P{p}</option>)}
                </select>
                <Button size="sm" onClick={assignArea} className="text-[10px]" data-testid="area-assign-btn">Apply</Button>
              </div>
            </div>

            <div className="p-2.5 rounded-lg border" style={{ borderColor: '#ec4899', background: '#ec489908' }}>
              <p className="text-[10px] font-semibold mb-1" style={{ color: '#ec4899' }}>Priority Override</p>
              <div className="space-y-1">
                <div className="flex gap-1">
                  <select value={overrideForm.lineId} onChange={e => setOverrideForm(p => ({ ...p, lineId: e.target.value }))}
                    className="text-[10px] px-2 py-1 rounded border flex-1" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                    <option value="">Select Line</option>
                    {allLines.map(l => <option key={l.lineId} value={l.lineId}>{l.lineId}</option>)}
                  </select>
                  <select value={overrideForm.priority} onChange={e => setOverrideForm(p => ({ ...p, priority: Number(e.target.value) }))}
                    className="text-[10px] px-2 py-1 rounded border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                    {[1,2,3,4,5].map(p => <option key={p} value={p}>P{p}</option>)}
                  </select>
                </div>
                <input value={overrideForm.reason} onChange={e => setOverrideForm(p => ({ ...p, reason: e.target.value }))}
                  placeholder="Reason (required)" className="w-full text-[10px] px-2 py-1 rounded border"
                  style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                <Button size="sm" onClick={applyOverride} disabled={!overrideForm.lineId || !overrideForm.reason}
                  className="text-[10px]" data-testid="override-btn">Apply Override</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ STEP 3: SUB-PRIORITY ═══ */}
      {step === 3 && (
        <div className="space-y-3" data-testid="wizard-step3">
          <div className="p-2.5 rounded-lg border" style={{ background: '#143A6B08', borderColor: '#143A6B20' }}>
            <p className="text-[10px] font-semibold mb-1" style={{ color: '#143A6B' }}>Tiebreaker Rules (System Defaults)</p>
            <div className="text-[9px] space-y-0.5" style={{ color: 'var(--text-muted)' }}>
              <p>1. Priority ASC — P1 first, P5 last</p>
              <p>2. Sub-Priority ASC — Lower sub-priority first</p>
              <p>3. Total Inch-Dia DESC — Larger inch-dia first (tiebreaker)</p>
              <p>4. Line ID Alpha ASC — Final stable tiebreaker</p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-medium" style={{ color: 'var(--text-primary)' }}>Priority Level:</span>
            {[1,2,3,4,5].map(p => (
              <button key={p} onClick={() => setSubPriLevel(p)} data-testid={`subpri-level-${p}`}
                className="text-[10px] px-2.5 py-1 rounded-md font-medium" style={{
                  background: subPriLevel === p ? PRI_COLORS[p].text : 'transparent',
                  color: subPriLevel === p ? '#fff' : PRI_COLORS[p].text,
                  border: `1px solid ${PRI_COLORS[p].text}40`,
                }}>P{p} ({allLines.filter(l => getEffectivePriority(l.lineId) === p).length})</button>
            ))}
          </div>

          <div className="flex gap-2 mb-2">
            <Button size="sm" variant="outline" onClick={() => autoSubPriority('inch-dia')} className="text-[10px]" data-testid="auto-sub-inch-dia">Auto by Inch-Dia</Button>
            <Button size="sm" variant="outline" onClick={() => autoSubPriority('area')} className="text-[10px]" data-testid="auto-sub-area">Auto by Area</Button>
          </div>

          <div className="rounded-lg border overflow-y-auto" style={{ borderColor: 'var(--border-color)', maxHeight: 320 }}>
            <table className="w-full text-[10px]">
              <thead>
                <tr style={{ background: 'var(--card-bg-alt)', position: 'sticky', top: 0, zIndex: 2 }}>
                  <th className="p-1.5 text-center font-medium w-10" style={{ color: 'var(--text-muted)' }}>Sub-P</th>
                  <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Line ID</th>
                  <th className="p-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>AG/UG</th>
                  <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Area</th>
                  <th className="p-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Total ID</th>
                  <th className="p-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Spools</th>
                  <th className="p-1.5 w-16" />
                </tr>
              </thead>
              <tbody>
                {subPriLines.map((l, i) => (
                  <tr key={l.lineId} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td className="p-1.5 text-center font-bold font-mono" style={{ color: PRI_COLORS[subPriLevel]?.text }}>{i + 1}</td>
                    <td className="p-1.5 font-mono font-semibold" style={{ color: 'var(--accent-primary)' }}>{l.lineId}</td>
                    <td className="p-1.5 text-center" style={{ color: l.agUg === 'AG' ? '#3b82f6' : '#f59e0b' }}>{l.agUg}</td>
                    <td className="p-1.5" style={{ color: 'var(--text-muted)' }}>{l.area}</td>
                    <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(l.totalLineInchDia)}</td>
                    <td className="p-1.5 text-center" style={{ color: 'var(--text-muted)' }}>{l.totalComponentCount || '-'}</td>
                    <td className="p-1.5">
                      <div className="flex gap-0.5">
                        <button onClick={() => {
                          if (i > 0) {
                            const updated = { ...assignments };
                            const above = subPriLines[i - 1];
                            updated[l.lineId] = { priority: subPriLevel, subPriority: i };
                            updated[above.lineId] = { priority: subPriLevel, subPriority: i + 1 };
                            setAssignments(updated);
                          }
                        }} disabled={i === 0} className="p-0.5 rounded hover:bg-black/5 disabled:opacity-30" data-testid={`subpri-up-${l.lineId}`}>
                          <ArrowUp className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                        </button>
                        <button onClick={() => {
                          if (i < subPriLines.length - 1) {
                            const updated = { ...assignments };
                            const below = subPriLines[i + 1];
                            updated[l.lineId] = { priority: subPriLevel, subPriority: i + 2 };
                            updated[below.lineId] = { priority: subPriLevel, subPriority: i + 1 };
                            setAssignments(updated);
                          }
                        }} disabled={i === subPriLines.length - 1} className="p-0.5 rounded hover:bg-black/5 disabled:opacity-30" data-testid={`subpri-down-${l.lineId}`}>
                          <ArrowDown className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ STEP 4: IMPACT PREVIEW ═══ */}
      {step === 4 && (
        <div className="space-y-3" data-testid="wizard-step4">
          {!preview && (
            <div className="text-center py-8">
              <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                Simulate allocation with your new priority assignments to preview impact.
              </p>
              <Button size="sm" onClick={loadPreview} disabled={previewLoading} data-testid="run-preview-btn">
                {previewLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                Run Impact Preview
              </Button>
            </div>
          )}

          {preview && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {[
                  { label: 'Gained Allocation', val: preview.summary?.gained, color: '#22c55e' },
                  { label: 'Lost Allocation', val: preview.summary?.lost, color: '#ef4444' },
                  { label: 'Unchanged', val: preview.summary?.unchanged, color: '#94a3b8' },
                  { label: 'Entering Workfront', val: preview.summary?.newlyEnteringWorkfront, color: '#3b82f6' },
                  { label: 'Exiting Workfront', val: preview.summary?.exitingWorkfront, color: '#f59e0b' },
                ].map(c => (
                  <div key={c.label} className="p-2.5 rounded-lg border" style={{ borderColor: 'var(--border-color)', background: 'var(--card-bg)' }}>
                    <p className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{c.label}</p>
                    <p className="text-lg font-bold" style={{ color: c.color }}>{c.val || 0}</p>
                  </div>
                ))}
              </div>

              {(preview.changes || []).length > 0 && (
                <div className="rounded-lg border overflow-y-auto" style={{ borderColor: 'var(--border-color)', maxHeight: 300 }}>
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr style={{ background: 'var(--card-bg-alt)', position: 'sticky', top: 0 }}>
                        <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Line ID</th>
                        <th className="p-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Priority</th>
                        <th className="p-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Sub-P</th>
                        <th className="p-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Hard%</th>
                        <th className="p-1.5 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Status</th>
                        <th className="p-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Inch-Dia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.changes.map(c => (
                        <tr key={c.lineId} style={{ borderTop: '1px solid var(--border-color)' }}>
                          <td className="p-1.5 font-mono font-semibold" style={{ color: 'var(--accent-primary)' }}>{c.lineId}</td>
                          <td className="p-1.5 text-center font-bold">P{c.priority}</td>
                          <td className="p-1.5 text-center">{c.subPriority || '—'}</td>
                          <td className="p-1.5 text-center font-mono font-bold" style={{ color: c.hardAllocPct >= 100 ? '#22c55e' : c.hardAllocPct > 0 ? '#f59e0b' : '#ef4444' }}>{c.hardAllocPct?.toFixed(0)}%</td>
                          <td className="p-1.5 text-center text-[9px]">{c.workfrontStatus?.replace(/_/g, ' ')}</td>
                          <td className="p-1.5 text-right font-mono text-[9px]">{c.totalLineInchDia?.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══ STEP 5: CONFIRM & APPLY ═══ */}
      {step === 5 && (
        <div className="space-y-3" data-testid="wizard-step5">
          <div className="p-4 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
            <h4 className="text-sm font-bold mb-3" style={{ color: 'var(--text-primary)' }}>Change Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[10px]">
              <div><p style={{ color: 'var(--text-muted)' }}>Total Assignments</p><p className="text-lg font-bold" style={{ color: '#143A6B' }}>{Object.keys(assignments).length}</p></div>
              <div><p style={{ color: 'var(--text-muted)' }}>Overrides</p><p className="text-lg font-bold" style={{ color: '#ec4899' }}>{overrides.length}</p></div>
              <div><p style={{ color: 'var(--text-muted)' }}>Unassigned Remaining</p><p className="text-lg font-bold" style={{ color: unassignedCount > 0 ? '#ef4444' : '#22c55e' }}>{unassignedCount}</p></div>
              <div><p style={{ color: 'var(--text-muted)' }}>Priority Distribution</p>
                <div className="flex gap-1 mt-1">{[1,2,3,4,5].map(p => (
                  <span key={p} className="text-[8px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: PRI_COLORS[p].bg, color: PRI_COLORS[p].text }}>
                    P{p}: {allLines.filter(l => getEffectivePriority(l.lineId) === p).length}
                  </span>
                ))}</div>
              </div>
            </div>

            <div className="mt-3 p-2 rounded-lg" style={{ background: '#22c55e08', border: '1px solid #22c55e30' }}>
              <div className="text-[10px] space-y-0.5" style={{ color: '#22c55e' }}>
                <p>Post-Apply Actions:</p>
                <p>- Hard Allocation engine will be triggered automatically</p>
                <p>- Workfront statuses will be recalculated</p>
                <p>- All changes logged in allocation ledger</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setStep(4)} className="text-[10px]">
              <ChevronLeft className="w-3 h-3 mr-1" /> Back
            </Button>
            <Button size="sm" onClick={applyAll} disabled={applying} data-testid="apply-all-btn"
              className="text-[10px]" style={{ background: '#22c55e', color: '#fff' }}>
              {applying ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              Apply All Changes & Run Allocation
            </Button>
          </div>
        </div>
      )}

      {/* Navigation */}
      {step !== 5 && (
        <div className="flex justify-between">
          <Button size="sm" variant="outline" onClick={() => step > 1 ? setStep(step - 1) : onClose?.()} className="text-[10px]">
            <ChevronLeft className="w-3 h-3 mr-1" /> {step === 1 ? 'Close' : 'Back'}
          </Button>
          <Button size="sm" onClick={() => setStep(step + 1)} className="text-[10px]">
            Next <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
